/**
 * PopoverMultiSelect Hook
 *
 * Manages a multi-select dropdown built on the HTML Popover API.
 *
 * Selection state is held client-side so the popover stays open while the
 * user picks multiple items. Each toggle pushes a LiveView event; the server
 * sends back the authoritative `data-values` which is synced in `updated()`.
 *
 * The popover element itself carries `phx-update="ignore"` so morphdom never
 * touches it — keeping it open across LiveView re-renders.
 */

const MENU_GAP = 4
const VIEWPORT_PADDING = 8

const PopoverMultiSelectHook = {
  mounted() {
    const { popoverId, buttonId, event, placeholder } = this.el.dataset

    this.popover = document.getElementById(popoverId)
    this.button = document.getElementById(buttonId)
    this.labelEl = document.getElementById(`${popoverId}-label`)
    this.event = event
    this.placeholder = placeholder
    this.selectedValues = JSON.parse(this.el.dataset.values || "[]")

    if (!this.isPopoverSupported()) {
      console.warn(
        "PopoverMultiSelect: Popover API not supported in this browser.",
      )
      return
    }

    // Position the popover after the browser has painted it
    this.popover.addEventListener("toggle", (e) => {
      if (e.newState === "open")
        requestAnimationFrame(() => this.positionDropdown())
    })

    // Handle option clicks inside the popover
    this.popover.addEventListener("click", (e) => {
      const li = e.target.closest("li[data-value]")
      if (!li) return

      const value = li.dataset.value

      if (value === "__all__") {
        this.selectedValues = []
      } else if (this.selectedValues.includes(value)) {
        this.selectedValues = this.selectedValues.filter((v) => v !== value)
      } else {
        this.selectedValues = [...this.selectedValues, value]
      }

      this.syncSelectionState()
      this.syncButtonLabel()
      this.pushEvent(this.event, { type: value })
    })

    // Sync initial checkbox states (server may have rendered stale markup
    // if the component was re-used with pre-existing values)
    this.syncSelectionState()
    this.syncButtonLabel()
  },

  updated() {
    // The wrapper re-rendered — sync options first (rep type may have changed),
    // then reconcile selected values from the server-authoritative data-values.
    this._syncOptions()
    this.selectedValues = JSON.parse(this.el.dataset.values || "[]")
    this.syncSelectionState()
    this.syncButtonLabel()
  },

  syncSelectionState() {
    if (!this.popover) return

    this.popover.querySelectorAll("li[data-value]").forEach((li) => {
      const isSelected =
        li.dataset.value === "__all__"
          ? this.selectedValues.length === 0
          : this.selectedValues.includes(li.dataset.value)

      // Show/hide the checkmark span
      const check = li.querySelector("[data-check]")
      if (check) check.style.display = isSelected ? "" : "none"

      // Highlight the row
      li.classList.toggle("bg-blue-50", isSelected)
      li.classList.toggle("font-medium", isSelected)
    })
  },

  syncButtonLabel() {
    if (!this.labelEl) return

    if (this.selectedValues.length === 0) {
      this.labelEl.textContent = this.placeholder
    } else if (this.selectedValues.length === 1) {
      const li = this.popover?.querySelector(
        `li[data-value="${this.selectedValues[0]}"]`,
      )
      this.labelEl.textContent = li?.dataset.label ?? this.selectedValues[0]
    } else {
      this.labelEl.textContent = `${this.selectedValues.length} selected`
    }
  },

  positionDropdown() {
    if (!this.button || !this.popover) return

    const buttonRect = this.button.getBoundingClientRect()
    const menuRect = this.popover.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Align right edge of menu to right edge of button
    let left = buttonRect.right - menuRect.width
    let top = buttonRect.bottom + MENU_GAP

    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING
    if (left + menuRect.width > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - menuRect.width - VIEWPORT_PADDING
    }
    if (top + menuRect.height > viewportHeight - VIEWPORT_PADDING) {
      top = buttonRect.top - menuRect.height - MENU_GAP
    }
    if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING

    this.popover.style.position = "fixed"
    this.popover.style.top = `${top}px`
    this.popover.style.left = `${left}px`
  },

  isPopoverSupported() {
    return (
      typeof HTMLElement !== "undefined" &&
      typeof HTMLElement.prototype.showPopover === "function"
    )
  },

  _syncOptions() {
    if (!this.popover) return

    const newOptions = JSON.parse(this.el.dataset.options || "[]")
    const existingLis = [
      ...this.popover.querySelectorAll(
        "li[data-value]:not([data-value='__all__'])",
      ),
    ]
    const existingOptions = existingLis.map((li) => ({
      value: li.dataset.value,
      label: li.dataset.label,
    }))

    if (JSON.stringify(newOptions) === JSON.stringify(existingOptions)) return

    existingLis.forEach((li) => li.remove())

    const ul = this.popover.querySelector("ul")
    newOptions.forEach(({ value, label }) => {
      const li = document.createElement("li")
      li.dataset.value = value
      li.dataset.label = label
      li.className = [
        "flex items-center justify-between -mx-[16px] px-[16px] py-[8px]",
        "text-[length:0.875rem] text-black cursor-pointer select-none",
        "hover:bg-blue-50",
      ].join(" ")
      li.appendChild(document.createTextNode(label))
      const check = document.createElement("span")
      check.dataset.check = ""
      check.style.display = "none"
      check.className = "text-blue-500"
      check.textContent = "✓"
      li.appendChild(check)
      ul.appendChild(li)
    })
  },
}

export default PopoverMultiSelectHook
